import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "../components/ProtectedRoute";

import type { JSX } from "react";

// Route components are code-split: each page becomes its own chunk loaded on demand,
// so the initial bundle stays small.
const Welcome = lazy(() => import("../pages/Welcome"));
const UserProfile = lazy(() => import("../pages/UserProfile"));
const Home = lazy(() => import("../pages/Home"));
const Info = lazy(() => import("../pages/Info"));
const Login = lazy(() => import("../pages/Login"));
const RegisterUser = lazy(() => import("../pages/RegisterUser"));
const RegisterArtist = lazy(() => import("../pages/RegisterArtist"));
const RegisterConfirmed = lazy(() => import("../pages/RegisterConfirmed"));
const MyProfile = lazy(() => import("../pages/MyProfile"));
const Settings = lazy(() => import("../pages/Settings"));
const AdminConsole = lazy(() => import("../pages/AdminConsole"));
const SearchResults = lazy(() => import("../pages/SearchResults"));
const MapSearchResults = lazy(() => import("../pages/MapSearchResults"));
const EmailVerification = lazy(() => import("../pages/EmailVerification"));

type MyRoute = Record<
  string,
  {
    path: string;
    element: JSX.Element;
    isAuthorized: boolean;
  }
>;

export const routes: MyRoute = {
  home: {
    path: "/home",
    element: <Home />,
    isAuthorized: true,
  },
  search: {
    path: "/search",
    element: <SearchResults />,
    isAuthorized: true,
  },
  mapSearch: {
    path: "/map-search",
    element: <MapSearchResults />,
    isAuthorized: true,
  },
  myProfile: {
    path: "/my-profile",
    element: <MyProfile />,
    isAuthorized: true,
  },
  settings: {
    path: "/settings",
    element: <Settings />,
    isAuthorized: true,
  },
  artistProfile: {
    path: "/user/:id",
    element: <UserProfile />,
    isAuthorized: true,
  },
  welcome: {
    path: "/",
    element: <Welcome />,
    isAuthorized: false,
  },
  registerUser: {
    path: "/register/user",
    element: <RegisterUser />,
    isAuthorized: false,
  },
  registerArtist: {
    path: "/register/artist",
    element: <RegisterArtist />,
    isAuthorized: false,
  },
  registerConfirmed: {
    path: "/register/confirmed",
    element: <RegisterConfirmed />,
    isAuthorized: false,
  },
  emailVerification: {
    path: "/email-verification",
    element: <EmailVerification />,
    isAuthorized: false,
  },
  login: {
    path: "/login",
    element: <Login />,
    isAuthorized: false,
  },
  info: {
    path: "/info",
    element: <Info />,
    isAuthorized: false,
  },
  adminConsole: {
    path: "/admin",
    element: <AdminConsole />,
    isAuthorized: true,
  },
  default: {
    path: "*",
    element: <Navigate to="/home" />,
    isAuthorized: false,
  },
};

function MyRoutes() {
  return (
    <Suspense
      fallback={
        <div className="ih-route-loading" role="status">
          Loading…
        </div>
      }
    >
      <Routes>
        {Object.values(routes).map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.isAuthorized ? (
                <ProtectedRoute>{route.element}</ProtectedRoute>
              ) : (
                route.element
              )
            }
          />
        ))}
      </Routes>
    </Suspense>
  );
}

export default MyRoutes;
